import { useState, useCallback, useEffect } from 'react';
import { IconPlus, IconCheck, IconLoader2, IconAlertCircle } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import { useMedications } from '../hooks/useMedications';
import { useSectionCompletion } from '../hooks/useSectionCompletion';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { MedicationForm } from '../components/medications/MedicationForm';
import { SupplementForm } from '../components/medications/SupplementForm';
import { AllergyForm } from '../components/medications/AllergyForm';
import type { MedicationLocal, SupplementLocal, AllergyLocal, MedicationsData } from '../types/medications';

// Extended types to track new (unsaved) items
interface MedicationWithNew extends MedicationLocal {
  isNewItem?: boolean;
}

interface SupplementWithNew extends SupplementLocal {
  isNewItem?: boolean;
}

interface AllergyWithNew extends AllergyLocal {
  isNewItem?: boolean;
}

// Factory functions for new items
function createEmptyMedication(): MedicationWithNew {
  return {
    localId: uuidv4(),
    medicationName: '',
    isNewItem: true,
  };
}

function createEmptySupplement(): SupplementWithNew {
  return {
    localId: uuidv4(),
    supplementName: '',
    isNewItem: true,
  };
}

function createEmptyAllergy(): AllergyWithNew {
  return {
    localId: uuidv4(),
    allergyName: '',
    isNewItem: true,
  };
}

export function Medications() {
  const { returnToDashboard } = useSectionCompletion('medications');
  const {
    medicationsData,
    isLoading,
    isSaving,
    error,
    hasMedications,
    hasSupplements,
    hasAllergies,
    reviewedFlags,
    setReviewedFlag,
    saveAll,
  } = useMedications();

  // Local state for editing
  const [medications, setMedications] = useState<MedicationWithNew[]>([]);
  const [supplements, setSupplements] = useState<SupplementWithNew[]>([]);
  const [allergies, setAllergies] = useState<AllergyWithNew[]>([]);

  // Sync local state when data loads
  useEffect(() => {
    if (!isLoading) {
      setMedications(
        medicationsData.medications.map((m) => ({ ...m, isNewItem: false }))
      );
      setSupplements(
        medicationsData.supplements.map((s) => ({ ...s, isNewItem: false }))
      );
      setAllergies(
        medicationsData.allergies.map((a) => ({ ...a, isNewItem: false }))
      );
    }
  }, [isLoading, medicationsData]);

  // === MEDICATION HANDLERS ===
  const handleAddMedication = useCallback(() => {
    setMedications((prev) => [...prev, createEmptyMedication()]);
  }, []);

  const handleUpdateMedication = useCallback(
    (localId: string, updated: MedicationWithNew) => {
      setMedications((prev) =>
        prev.map((m) => (m.localId === localId ? updated : m))
      );
    },
    []
  );

  const handleRemoveMedication = useCallback((localId: string) => {
    setMedications((prev) => {
      const item = prev.find((m) => m.localId === localId);
      if (item?.isNewItem) {
        return prev.filter((m) => m.localId !== localId);
      }
      return prev.map((m) =>
        m.localId === localId ? { ...m, isDeleted: true } : m
      );
    });
  }, []);

  const handleSubmitMedication = useCallback((localId: string) => {
    setMedications((prev) =>
      prev.map((m) =>
        m.localId === localId ? { ...m, isNewItem: false } : m
      )
    );
  }, []);

  // === SUPPLEMENT HANDLERS ===
  const handleAddSupplement = useCallback(() => {
    setSupplements((prev) => [...prev, createEmptySupplement()]);
  }, []);

  const handleUpdateSupplement = useCallback(
    (localId: string, updated: SupplementWithNew) => {
      setSupplements((prev) =>
        prev.map((s) => (s.localId === localId ? updated : s))
      );
    },
    []
  );

  const handleRemoveSupplement = useCallback((localId: string) => {
    setSupplements((prev) => {
      const item = prev.find((s) => s.localId === localId);
      if (item?.isNewItem) {
        return prev.filter((s) => s.localId !== localId);
      }
      return prev.map((s) =>
        s.localId === localId ? { ...s, isDeleted: true } : s
      );
    });
  }, []);

  const handleSubmitSupplement = useCallback((localId: string) => {
    setSupplements((prev) =>
      prev.map((s) =>
        s.localId === localId ? { ...s, isNewItem: false } : s
      )
    );
  }, []);

  // === ALLERGY HANDLERS ===
  const handleAddAllergy = useCallback(() => {
    setAllergies((prev) => [...prev, createEmptyAllergy()]);
  }, []);

  const handleUpdateAllergy = useCallback(
    (localId: string, updated: AllergyWithNew) => {
      setAllergies((prev) =>
        prev.map((a) => (a.localId === localId ? updated : a))
      );
    },
    []
  );

  const handleRemoveAllergy = useCallback((localId: string) => {
    setAllergies((prev) => {
      const item = prev.find((a) => a.localId === localId);
      if (item?.isNewItem) {
        return prev.filter((a) => a.localId !== localId);
      }
      return prev.map((a) =>
        a.localId === localId ? { ...a, isDeleted: true } : a
      );
    });
  }, []);

  const handleSubmitAllergy = useCallback((localId: string) => {
    setAllergies((prev) =>
      prev.map((a) =>
        a.localId === localId ? { ...a, isNewItem: false } : a
      )
    );
  }, []);

  // === TOGGLE "NONE TO ADD" ===
  const handleToggleNoMedications = useCallback(() => {
    setReviewedFlag('noMedications', !reviewedFlags.noMedications);
  }, [reviewedFlags.noMedications, setReviewedFlag]);

  const handleToggleNoSupplements = useCallback(() => {
    setReviewedFlag('noSupplements', !reviewedFlags.noSupplements);
  }, [reviewedFlags.noSupplements, setReviewedFlag]);

  const handleToggleNoAllergies = useCallback(() => {
    setReviewedFlag('noAllergies', !reviewedFlags.noAllergies);
  }, [reviewedFlags.noAllergies, setReviewedFlag]);

  // === SAVE ===
  const handleSave = async () => {
    const data: MedicationsData = {
      medications: medications.filter((m) => !m.isNewItem),
      supplements: supplements.filter((s) => !s.isNewItem),
      allergies: allergies.filter((a) => !a.isNewItem),
    };

    const success = await saveAll(data);
    if (success) {
      showSuccessToast('Medications & allergies saved');
      returnToDashboard();
    } else {
      showErrorToast('Unable to save. Please try again.');
    }
  };

  // Check for unsaved items (still in edit mode)
  const hasUnsavedItems =
    medications.some((m) => m.isNewItem && !m.isDeleted) ||
    supplements.some((s) => s.isNewItem && !s.isDeleted) ||
    allergies.some((a) => a.isNewItem && !a.isDeleted);

  // Navigation guard for unsaved items
  useUnsavedChangesGuard(hasUnsavedItems);

  // Visible items (filter out deleted)
  const visibleMedications = medications.filter((m) => !m.isDeleted);
  const visibleSupplements = supplements.filter((s) => !s.isDeleted);
  const visibleAllergies = allergies.filter((a) => !a.isDeleted);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <IconLoader2 className="animate-spin text-cyan-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Medications, Supplements, & Allergies
        </h2>
        <p className="text-gray-400">
          List your current medications, supplements, and any allergies you have.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
          <IconAlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Section 1: Current Medications */}
      <section>
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
            1
          </span>
          Current Medications
        </h3>

        <div className="pl-8 space-y-4">
          {/* Empty state */}
          {visibleMedications.length === 0 && !hasMedications && (
            <div className="p-4 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
              No medications added yet. Add any prescription or over-the-counter medications you take regularly.
            </div>
          )}

          {/* Medication items */}
          {visibleMedications.map((med) => (
            <MedicationForm
              key={med.localId}
              medication={med}
              onChange={(updated) => handleUpdateMedication(med.localId, updated)}
              onRemove={() => handleRemoveMedication(med.localId)}
              onSubmit={() => handleSubmitMedication(med.localId)}
              isNew={med.isNewItem}
              disabled={isSaving}
            />
          ))}

          {/* Add button + None to add */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddMedication}
              disabled={isSaving || reviewedFlags.noMedications}
              className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <IconPlus size={16} />
              Add Medication
            </button>

            {!hasMedications && (
              <button
                type="button"
                onClick={handleToggleNoMedications}
                disabled={isSaving}
                className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                  reviewedFlags.noMedications
                    ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                    : 'border border-dashed border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <IconCheck size={16} />
                None to add
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Section 2: Supplements & Vitamins */}
      <section>
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
            2
          </span>
          Supplements & Vitamins
        </h3>

        <div className="pl-8 space-y-4">
          {/* Empty state */}
          {visibleSupplements.length === 0 && !hasSupplements && (
            <div className="p-4 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
              No supplements added yet. Add any vitamins, supplements, or herbal remedies you take.
            </div>
          )}

          {/* Supplement items */}
          {visibleSupplements.map((supp) => (
            <SupplementForm
              key={supp.localId}
              supplement={supp}
              onChange={(updated) => handleUpdateSupplement(supp.localId, updated)}
              onRemove={() => handleRemoveSupplement(supp.localId)}
              onSubmit={() => handleSubmitSupplement(supp.localId)}
              isNew={supp.isNewItem}
              disabled={isSaving}
            />
          ))}

          {/* Add button + None to add */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddSupplement}
              disabled={isSaving || reviewedFlags.noSupplements}
              className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <IconPlus size={16} />
              Add Supplement
            </button>

            {!hasSupplements && (
              <button
                type="button"
                onClick={handleToggleNoSupplements}
                disabled={isSaving}
                className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                  reviewedFlags.noSupplements
                    ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                    : 'border border-dashed border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <IconCheck size={16} />
                None to add
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Section 3: Allergies & Sensitivities */}
      <section>
        <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
            3
          </span>
          Allergies & Sensitivities
        </h3>

        <div className="pl-8 space-y-4">
          {/* Empty state */}
          {visibleAllergies.length === 0 && !hasAllergies && (
            <div className="p-4 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
              No allergies added yet. Add any drug, food, or environmental allergies or sensitivities.
            </div>
          )}

          {/* Allergy items */}
          {visibleAllergies.map((allergy) => (
            <AllergyForm
              key={allergy.localId}
              allergy={allergy}
              onChange={(updated) => handleUpdateAllergy(allergy.localId, updated)}
              onRemove={() => handleRemoveAllergy(allergy.localId)}
              onSubmit={() => handleSubmitAllergy(allergy.localId)}
              isNew={allergy.isNewItem}
              disabled={isSaving}
            />
          ))}

          {/* Add button + None to add */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddAllergy}
              disabled={isSaving || reviewedFlags.noAllergies}
              className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <IconPlus size={16} />
              Add Allergy or Sensitivity
            </button>

            {!hasAllergies && (
              <button
                type="button"
                onClick={handleToggleNoAllergies}
                disabled={isSaving}
                className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                  reviewedFlags.noAllergies
                    ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                    : 'border border-dashed border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <IconCheck size={16} />
                None to add
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-800">
        <div>
          {hasUnsavedItems && (
            <p className="text-sm text-yellow-500">
              Please save or cancel all items before proceeding.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || hasUnsavedItems}
          className="btn disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving && <IconLoader2 className="animate-spin" size={16} />}
          Save
        </button>
      </div>
    </div>
  );
}
